// 添付ダウンロードとクラウド転送操作
import { ref } from 'vue';
import _ from 'lodash';

export const useExportActions = () => {
  const attachmentArchives = ref([]);
  const exportingIds = ref(new Set());

  const openAttachmentDownloadModal = (video) => {
    if (!video) {
      return;
    }

    const archives = Array.isArray(video.attachment_archives)
      ? video.attachment_archives
      : [];
    attachmentArchives.value = archives;

    if (archives.length === 0) {
      if (video.attachments_download_url) {
        window.location.href = video.attachments_download_url;
      }
      return;
    }

    if (archives.length === 1) {
      window.location.href = archives[0].url;
      return;
    }

    if (window.jQuery) {
      window.jQuery('#attachmentDownloadModal').modal('show');
    }
  };

  const downloadFile = (url) =>
    new Promise((resolve) => {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = '';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      resolve();
    });

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const downloadAllArchives = async () => {
    for (const archive of attachmentArchives.value) {
      if (!archive || !archive.url) {
        continue;
      }
      await downloadFile(archive.url);
      await sleep(1000);
    }
  };

  const makeExportKey = (videoId, provider) =>
    `${provider}:${String(videoId ?? '')}`;

  const isExporting = (videoId, provider) =>
    exportingIds.value.has(makeExportKey(videoId, provider));

  const setExporting = (videoId, provider, status) => {
    const normalizedId = String(videoId ?? '');
    if (!normalizedId || !provider) {
      return;
    }

    const next = new Set(exportingIds.value);
    const key = makeExportKey(normalizedId, provider);
    if (status) {
      next.add(key);
    } else {
      next.delete(key);
    }
    exportingIds.value = next;
  };

  const translate = (key, fallback = null) =>
    _.get(window.i18n, key) || fallback || key;

  const resolveApiErrorMessage = (error, fallbackKey) => {
    const responseMessage = error?.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage !== '') {
      return translate(responseMessage, responseMessage);
    }

    return translate(fallbackKey);
  };

  const exportToOneDrive = async (videoId, confirmMessage) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }
    const normalizedId = String(videoId ?? '');
    if (!normalizedId || isExporting(normalizedId, 'onedrive')) {
      return;
    }

    setExporting(normalizedId, 'onedrive', true);

    try {
      await window.axios.post(`/api/videos/${normalizedId}/export-to-onedrive`, { drive: 'onedrive' });
      window.alert(
        translate('Transfer started. We will notify you by email when completed.')
      );
    } catch (error) {
      window.alert(resolveApiErrorMessage(error, 'OneDrive transfer failed to start.'));
    } finally {
      setExporting(normalizedId, 'onedrive', false);
    }
  };

  const exportToSharePoint = async (videoId, confirmMessage) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }
    const normalizedId = String(videoId ?? '');
    if (!normalizedId || isExporting(normalizedId, 'sharepoint')) {
      return;
    }

    setExporting(normalizedId, 'sharepoint', true);

    try {
      await window.axios.post(`/api/videos/${normalizedId}/export-to-onedrive`, { drive: 'sharepoint' });
      window.alert(
        translate('Transfer started. We will notify you by email when completed.')
      );
    } catch (error) {
      window.alert(resolveApiErrorMessage(error, 'SharePoint transfer failed to start.'));
    } finally {
      setExporting(normalizedId, 'sharepoint', false);
    }
  };

  const exportToGoogleDrive = async (videoId, confirmMessage) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }
    const normalizedId = String(videoId ?? '');
    if (!normalizedId || isExporting(normalizedId, 'googledrive')) {
      return;
    }

    setExporting(normalizedId, 'googledrive', true);

    try {
      await window.axios.post(`/api/videos/${normalizedId}/export-to-googledrive`);
      window.alert(
        translate('Transfer started. We will notify you by email when completed.')
      );
    } catch (error) {
      window.alert(resolveApiErrorMessage(error, 'Google Drive transfer failed to start.'));
    } finally {
      setExporting(normalizedId, 'googledrive', false);
    }
  };

  const exportToDropbox = async (videoId, confirmMessage) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }
    const normalizedId = String(videoId ?? '');
    if (!normalizedId || isExporting(normalizedId, 'dropbox')) {
      return;
    }

    setExporting(normalizedId, 'dropbox', true);

    try {
      await window.axios.post(`/api/videos/${normalizedId}/export-to-dropbox`);
      window.alert(
        translate('Transfer started. We will notify you by email when completed.')
      );
    } catch (error) {
      window.alert(resolveApiErrorMessage(error, 'Dropbox transfer failed to start.'));
    } finally {
      setExporting(normalizedId, 'dropbox', false);
    }
  };

  const exportToBox = async (videoId, confirmMessage) => {
    if (!window.confirm(confirmMessage)) {
      return;
    }
    const normalizedId = String(videoId ?? '');
    if (!normalizedId || isExporting(normalizedId, 'box')) {
      return;
    }

    setExporting(normalizedId, 'box', true);

    try {
      await window.axios.post(`/api/videos/${normalizedId}/export-to-box`);
      window.alert(
        translate('Transfer started. We will notify you by email when completed.')
      );
    } catch (error) {
      window.alert(resolveApiErrorMessage(error, 'Box transfer failed to start.'));
    } finally {
      setExporting(normalizedId, 'box', false);
    }
  };

  return {
    attachmentArchives,
    openAttachmentDownloadModal,
    downloadAllArchives,
    exportToOneDrive,
    exportToSharePoint,
    exportToGoogleDrive,
    exportToDropbox,
    exportToBox,
    isExporting,
  };
};
